/**
 * src/services/remoteHydration.ts
 *
 * Remote manifest fetching, scheme security validation, relative URL resolution,
 * and strictly in-memory hydration without local database/OPFS persistence.
 */

import { PolarstepsLocationPoint, PolarstepsStep } from '../types/polarsteps';
import { PhotoRecord } from '../types/domain';
import { ExportBundleManifest } from './exportService';

export type { ExportBundleManifest };

export interface ParsedShareRoute {
  isShareMode: boolean;
  manifestUrl: string | null;
  rawParam: string | null;
  paramKey: 'trip' | 'manifest' | null;
  error?: string;
}

export interface HydratedTripData {
  tripName: string;
  timezoneId: string;
  startDate: number;
  endDate: number;
  photos: PhotoRecord[];
  locations: PolarstepsLocationPoint[];
  steps: PolarstepsStep[];
  manifestUrl: string;
}

/**
 * Extracts and sanitizes the remote manifest URL from window.location hash or query.
 * Blocks dangerous schemes (javascript:, data:, file:, blob:, vbscript:).
 */
export function parseShareRoute(
  loc: { hash?: string; search?: string; href?: string } = typeof window !== 'undefined'
    ? window.location
    : {}
): ParsedShareRoute {
  const hash = loc.hash || '';
  const search = loc.search || '';

  const extractParam = (queryString: string): { key: 'trip' | 'manifest'; value: string } | null => {
    if (!queryString) return null;
    const clean = queryString.replace(/^[#?]/, '').replace(/^\//, '');

    // 1. Try URLSearchParams
    try {
      const searchParams = new URLSearchParams(clean);
      if (searchParams.has('trip')) {
        return { key: 'trip', value: searchParams.get('trip')! };
      }
      if (searchParams.has('manifest')) {
        return { key: 'manifest', value: searchParams.get('manifest')! };
      }
    } catch {
      // Fallback regex
    }

    // 2. Direct regex matching fallback for "#trip=https://..." or "#manifest=https://..."
    const match = clean.match(/^(trip|manifest)=(.+)$/i);
    if (match) {
      return { key: match[1].toLowerCase() as 'trip' | 'manifest', value: match[2] };
    }

    return null;
  };

  const detected = extractParam(hash) || extractParam(search);

  if (!detected || !detected.value.trim()) {
    return {
      isShareMode: false,
      manifestUrl: null,
      rawParam: null,
      paramKey: null,
    };
  }

  const raw = detected.value.trim();

  // 1. Security Check: Block dangerous schemes to prevent XSS / local file sniffing
  const lower = raw.toLowerCase();
  const dangerousSchemes = ['javascript:', 'data:', 'file:', 'blob:', 'vbscript:'];
  if (dangerousSchemes.some((scheme) => lower.startsWith(scheme))) {
    return {
      isShareMode: true,
      manifestUrl: null,
      rawParam: raw,
      paramKey: detected.key,
      error: 'Protocollo URL non sicuro. Sono ammessi solo URL https://, http:// o percorsi relativi.',
    };
  }

  // 2. URL Resolution & Validation
  let resolvedUrl: string;
  try {
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      const parsed = new URL(raw);
      resolvedUrl = parsed.href;
    } else {
      // Relative path: resolve against base
      const base = loc.href || (typeof window !== 'undefined' ? window.location.href : 'http://localhost/');
      resolvedUrl = new URL(raw, base).href;
    }
  } catch {
    return {
      isShareMode: true,
      manifestUrl: null,
      rawParam: raw,
      paramKey: detected.key,
      error: 'Formato URL non valido per il manifest di viaggio.',
    };
  }

  return {
    isShareMode: true,
    manifestUrl: resolvedUrl,
    rawParam: raw,
    paramKey: detected.key,
  };
}

/**
 * Builds a shareable URL given a remote manifest URL.
 */
export function buildShareableUrl(manifestUrl: string): string {
  if (typeof window === 'undefined') return `#trip=${manifestUrl}`;
  const base = `${window.location.origin}${window.location.pathname}`;
  return `${base}#trip=${encodeURIComponent(manifestUrl)}`;
}

/**
 * Resolves an asset key against the manifest directory base URL.
 */
export function resolveAssetUrl(key: string, manifestBaseUrl: string): string {
  if (!key) return '';
  if (
    key.startsWith('http://') ||
    key.startsWith('https://') ||
    key.startsWith('data:') ||
    key.startsWith('blob:')
  ) {
    return key;
  }

  try {
    const baseDir = manifestBaseUrl.substring(0, manifestBaseUrl.lastIndexOf('/') + 1);
    const cleanKey = key.replace(/^\.\//, '').replace(/^\//, '');
    return new URL(cleanKey, baseDir).href;
  } catch {
    return key;
  }
}

/**
 * Validates that an arbitrary JSON object conforms to the ExportBundleManifest schema.
 */
export function validateManifestSchema(json: unknown): ExportBundleManifest {
  if (!json || typeof json !== 'object') {
    throw new Error('Il manifest non è un oggetto JSON valido.');
  }

  const manifest = json as any;

  if (!manifest.trip || typeof manifest.trip !== 'object') {
    throw new Error('Il manifest non contiene la sezione "trip" richiesta.');
  }

  if (!manifest.trip.name && typeof manifest.trip.id === 'undefined') {
    throw new Error('La sezione "trip" nel manifest è priva di nome o identificatore.');
  }

  return manifest as ExportBundleManifest;
}

/**
 * Hydrates trip metadata and photo records in-memory into React state.
 * Strictly guarantees ZERO writes or mutations to IndexedDB, Dexie, or OPFS.
 */
export function hydrateTripData(
  manifest: ExportBundleManifest,
  manifestUrl: string,
  externalPhotos?: PhotoRecord[]
): HydratedTripData {
  const trip = manifest.trip as any;
  const rawPhotos: PhotoRecord[] = (manifest as any).photos || externalPhotos || [];

  const normalizedPhotos: PhotoRecord[] = rawPhotos
    .filter((p) => p && typeof p.id === 'string')
    .map((p) => {
      const thumbUrl = resolveAssetUrl(p.thumbnailKey || `photos/thumb/${p.id}.webp`, manifestUrl);
      const prevUrl = resolveAssetUrl(p.previewKey || `photos/hd/${p.id}.webp`, manifestUrl);

      return {
        ...p,
        thumbnailKey: thumbUrl,
        previewKey: prevUrl,
      };
    });

  const locations: PolarstepsLocationPoint[] = Array.isArray(trip?.locations)
    ? [...trip.locations].sort((a, b) => a.time - b.time)
    : [];

  const steps: PolarstepsStep[] = Array.isArray(trip?.all_steps) ? trip.all_steps : [];

  return {
    tripName: trip?.name || 'Viaggio Polarsteps',
    timezoneId: trip?.timezone_id || 'UTC',
    startDate: trip?.start_date || 0,
    endDate: trip?.end_date || 0,
    photos: normalizedPhotos,
    locations,
    steps,
    manifestUrl,
  };
}
